import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { Sentry } from '../lib/sentry';
import { track } from '../lib/analytics';
import {
  attemptChunkRecoveryReload,
  forceChunkRecoveryReload,
  isChunkLoadError,
} from '../lib/chunk-recovery';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error) && attemptChunkRecoveryReload()) {
      return;
    }

    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
    track('frontend_error', {
      surface: 'react_error_boundary',
      message: error.message,
      name: error.name,
      component_stack: info.componentStack?.slice(0, 1000),
      path: window.location.pathname,
    });
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: info.componentStack,
        },
      },
    });
  }

  handleReset = () => {
    if (isChunkLoadError(this.state.error)) {
      forceChunkRecoveryReload();
      return;
    }

    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          bgcolor="background.default"
          px={3}
        >
          {/* Animated fuel-drop icon */}
          <Box sx={(theme) => ({
            width: 80, height: 80, borderRadius: 3,
            bgcolor: 'error.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: 3, boxShadow: `0 12px 40px ${alpha(theme.palette.error.main, 0.25)}`,
            animation: 'errorPulse 2s ease-in-out infinite',
            '@keyframes errorPulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.08)' },
            },
          })}>
            <LocalGasStationIcon sx={{ color: 'common.white', fontSize: 40 }} />
          </Box>

          <Typography variant="h3" fontWeight={800} color="error.main" mb={1}>
            Oops!
          </Typography>

          <Typography variant="h6" fontWeight={700} color="text.primary" mb={1}>
            Something went wrong
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={3} textAlign="center" maxWidth={400}>
            {isChunkLoadError(this.state.error)
              ? 'A new app version is available. Refresh to load the latest files.'
              : 'An unexpected error occurred. You can try again or head back to the dashboard.'}
          </Typography>

          {import.meta.env.DEV && this.state.error && (
            <Box
              component="pre"
              sx={(theme) => ({
                textAlign: 'left',
                bgcolor: alpha(theme.palette.error.main, 0.08),
                border: '1px solid',
                borderColor: alpha(theme.palette.error.main, 0.25),
                color: 'error.main',
                p: 2, borderRadius: 2, fontSize: 11,
                overflow: 'auto', mb: 3, maxHeight: 140, maxWidth: 500, width: '100%',
              })}
            >
              {this.state.error.message}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleReset}
              sx={{
                px: 3, py: 1.2, fontWeight: 700, borderRadius: 2,
              }}>
              {isChunkLoadError(this.state.error) ? 'Refresh app' : 'Try again'}
            </Button>
            <Button variant="outlined" startIcon={<HomeIcon />}
              onClick={() => { window.location.href = '/dashboard'; }}
              sx={(theme) => ({
                px: 3, py: 1.2, fontWeight: 700, borderRadius: 2,
                borderColor: 'divider', color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              })}>
              Dashboard
            </Button>
          </Box>

          <Typography variant="caption" color="text.disabled" mt={6}>
            PetroNexus &bull; If this keeps happening, contact support
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
